import { getTeamMembers, getTeams, updateTeam } from 'api/teams'
import NotificationSys from 'components/NotificationSystem'
import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react'
import { useMutation, useQuery } from 'react-query'
import { Nullish, Team, TeamMember } from 'types'

const TeamsContext = createContext<{
  teamsData: Team[]
  activeTeamId: Nullish<number>
  teamMembersData: TeamMember[]
  isTeamMembersLoading: boolean
  onChangeTeam: (teamId: Nullish<number>) => void
  onRefetchTeams: () => Promise<void>
  onRefetchTeamMembers: () => Promise<void>
} | null>(null)

export function TeamsProvider({ children }: { children: ReactNode }) {
  const teamsQuery = useQuery('getTeams', getTeams)

  const teamsData = useMemo(() => {
    if (!teamsQuery.data) return []

    return teamsQuery.data.data.rows
  }, [teamsQuery])

  const [activeTeamIdLocal, setActiveTeamIdLocal] = useState<Nullish<number>>(null)

  const activeTeamId = useMemo(() => {
    if (!teamsQuery.data) return null
    if (activeTeamIdLocal) return activeTeamIdLocal

    const activeTeam = teamsQuery.data.data.rows.find((team) => team.isActive)
    return activeTeam?.id || null
  }, [activeTeamIdLocal, teamsQuery])

  const updateTeamMutation = useMutation(updateTeam, {
    onSuccess: () => {
      NotificationSys.showSuccess(`Care Team successfully updated`)
    },
  })

  const saveActiveTeam = useCallback(
    async (teamId: number) => {
      try {
        await updateTeamMutation.mutateAsync({
          id: teamId,
          isActive: true,
        })
      } catch (e) {
        NotificationSys.showWarning('An error occurred while update the team')
        console.error(e)
      }
    },
    [updateTeamMutation],
  )

  const handleChangeTeam = useCallback(
    async (teamId: Nullish<number>) => {
      setActiveTeamIdLocal(teamId)
      if (teamId) {
        await saveActiveTeam(teamId)
      }
    },
    [saveActiveTeam],
  )

  const handleRefetchTeams = useCallback(async () => {
    await teamsQuery.refetch()
  }, [teamsQuery])

  const teamMemberQuery = useQuery(
    ['getTeamMember', activeTeamId],
    () => {
      if (!activeTeamId) return null

      return getTeamMembers({
        id: activeTeamId,
      })
    },
    {
      enabled: activeTeamId !== null,
    },
  )

  const teamMembersData = useMemo(() => {
    if (!teamMemberQuery.data) return []

    return teamMemberQuery.data.data.rows
  }, [teamMemberQuery])

  const handleRefetchTeamMembers = useCallback(async () => {
    await teamMemberQuery.refetch()
  }, [teamMemberQuery])

  return (
    <TeamsContext.Provider
      value={{
        activeTeamId,
        onChangeTeam: handleChangeTeam,
        teamsData,
        teamMembersData,
        isTeamMembersLoading: teamMemberQuery.isFetching,
        onRefetchTeams: handleRefetchTeams,
        onRefetchTeamMembers: handleRefetchTeamMembers,
      }}
    >
      {children}
    </TeamsContext.Provider>
  )
}

export function useTeams() {
  const context = useContext(TeamsContext)
  if (!context) {
    throw new Error('useTeams must be used within a TeamsProvider')
  }
  return context
}
